import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { stagger } from "@/lib/motion";

interface Props {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  wide?: boolean;
}

/** Screen wrapper with an iOS-style large title that fades into the compact bar on scroll. */
export function Page({ title, subtitle, actions, children, className, wide }: Props) {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const largeOpacity = useTransform(scrollY, [0, 48], [1, 0]);
  const compactOpacity = useTransform(scrollY, [32, 64], [0, 1]);

  return (
    <div
      className={cn(
        "mx-auto w-full pr-[max(1rem,env(safe-area-inset-right))] pb-[calc(env(safe-area-inset-bottom)+8rem)] pl-[max(1rem,env(safe-area-inset-left))] lg:px-8 lg:pb-12",
        wide ? "max-w-[1120px]" : "max-w-[720px]",
      )}
    >
      <motion.div
        style={{ opacity: reduce ? 1 : compactOpacity }}
        className="pointer-events-none fixed inset-x-0 top-0 z-30 flex h-[calc(env(safe-area-inset-top)+44px)] items-end justify-center safe-top pb-2.5 glass lg:left-[248px]"
        aria-hidden
      >
        <span className="text-headline">{title}</span>
      </motion.div>
      <header className="flex items-end justify-between gap-3 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-4 lg:pt-10">
        <motion.div style={{ opacity: reduce ? 1 : largeOpacity }} className="min-w-0">
          <h1 className="line-clamp-2 text-large-title text-balance">{title}</h1>
          {subtitle && <div className="mt-0.5 text-subhead text-muted">{subtitle}</div>}
        </motion.div>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </header>
      <motion.div
        variants={stagger()}
        initial="hidden"
        animate="show"
        className={cn("space-y-6", className)}
      >
        {children}
      </motion.div>
    </div>
  );
}
